import { validationMessages } from '@/shared/lib/utils/validation/messages';

export type DropzoneAccept = Record<string, readonly string[]>;

export type AllowedFileTypeOptions = {
    validateMime?: boolean;
};

const getFileExtension = (fileName: string): string => {
    const dotIndex = fileName.lastIndexOf('.');

    if (dotIndex === -1) {
        return '';
    }

    return fileName.slice(dotIndex).toLowerCase();
};

const matchesMimeType = (mimeType: string, rule: string): boolean => {
    const normalized = rule.toLowerCase();

    if (normalized.endsWith('/*')) {
        return mimeType.startsWith(normalized.slice(0, -1));
    }

    return mimeType === normalized;
};

const matchesAllowedRule = (extension: string, mimeType: string, rule: string): boolean => {
    const normalized = rule.toLowerCase();

    if (normalized.startsWith('.')) {
        return extension === normalized;
    }

    if (!mimeType) {
        return false;
    }

    return matchesMimeType(mimeType, normalized);
};

const isStrictAllowedFileType = (
    extension: string,
    mimeType: string,
    extensions: string[],
    mimeRules: string[],
): boolean => {
    const extensionAllowed = extensions.length === 0 || extensions.some((rule) => extension === rule.toLowerCase());

    if (!extensionAllowed) {
        return false;
    }

    if (mimeRules.length === 0 || !mimeType) {
        return true;
    }

    return mimeRules.some((rule) => matchesMimeType(mimeType, rule));
};

export const isAllowedFileType = (
    file: File,
    allowedFileTypes: string[],
    options?: AllowedFileTypeOptions,
): boolean => {
    if (allowedFileTypes.length === 0) {
        return true;
    }

    const extension = getFileExtension(file.name);
    const mimeType = file.type.toLowerCase();
    const extensions = allowedFileTypes.filter((type) => type.startsWith('.'));
    const mimeRules = allowedFileTypes.filter((type) => !type.startsWith('.'));

    if (options?.validateMime && mimeRules.length > 0) {
        return isStrictAllowedFileType(extension, mimeType, extensions, mimeRules);
    }

    return allowedFileTypes.some((allowed) => matchesAllowedRule(extension, mimeType, allowed));
};

export const getDropzoneAccept = (allowedFileTypes: string[]): DropzoneAccept | undefined => {
    if (allowedFileTypes.length === 0) {
        return undefined;
    }

    const extensions = allowedFileTypes.filter((type) => type.startsWith('.')).map((type) => type.toLowerCase());
    const mimeRules = allowedFileTypes.filter((type) => !type.startsWith('.'));

    if (mimeRules.length === 0) {
        return undefined;
    }

    return mimeRules.reduce<DropzoneAccept>((accept, mimeRule) => {
        accept[mimeRule.toLowerCase()] = extensions;
        return accept;
    }, {});
};

export const getInvalidFileTypeMessage = (allowedFileTypes: string[]): string => {
    const extensions = allowedFileTypes
        .filter((type) => type.startsWith('.'))
        .map((type) => type.slice(1).toUpperCase());
    const formats = extensions.length > 0 ? extensions.join(', ') : allowedFileTypes.join(', ');

    return validationMessages.ru.INVALIDATED_FILE_TYPE.replace('{formats}', formats);
};

export const getAllowedFormatsPlaceholder = (allowedFileTypes: string[]): string => {
    const extensions = allowedFileTypes
        .filter((type) => type.startsWith('.'))
        .map((type) => type.toLowerCase())
        .join(', ');

    return extensions ? `Прикрепите, файл в формате ${extensions}` : 'Прикрепите файл';
};
